import { useEffect, useRef, useState } from 'react'
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react'

import { ApiError } from '../../../services/api/client'
import { Button } from '../../../ui/Button/Button'
import type {
  InternalNote,
  InternalNotesRepository,
} from '../internal-notes-repository'
import styles from '../ConversationsPage.module.css'

type LoadState =
  | { status: 'loading'; notes: InternalNote[]; cursor: null }
  | { status: 'error'; notes: InternalNote[]; cursor: string | null }
  | { status: 'ready'; notes: InternalNote[]; cursor: string | null }

function idempotencyKey() {
  return `internal-note-${crypto.randomUUID()}`
}

function formatTimestamp(value: number) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value))
}

export function InternalNotesCard({
  tenantId,
  aggregateId,
  repository,
}: {
  tenantId: string
  aggregateId: string
  repository: InternalNotesRepository
}) {
  const [state, setState] = useState<LoadState>({
    status: 'loading',
    notes: [],
    cursor: null,
  })
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const operation = useRef<{ signature: string; key: string } | null>(null)

  const load = (cursor?: string) => {
    if (!cursor) {
      setState({ status: 'loading', notes: [], cursor: null })
    }
    void repository
      .list(tenantId, aggregateId, cursor)
      .then((result) => {
        if (result.aggregateId !== aggregateId)
          throw new Error('Wrong aggregate')
        setState((current) => ({
          status: 'ready',
          notes: cursor ? [...current.notes, ...result.notes] : result.notes,
          cursor: result.nextCursor,
        }))
      })
      .catch(() =>
        setState((current) => ({
          status: 'error',
          notes: cursor ? current.notes : [],
          cursor: cursor ?? null,
        }))
      )
  }

  useEffect(() => {
    setAdding(false)
    setEditing(null)
    setDraft('')
    setMutationError(null)
    operation.current = null
    load()
    // The repository is stable for the authenticated session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregateId, repository, tenantId])

  const keyFor = (signature: string) => {
    if (operation.current?.signature !== signature) {
      operation.current = { signature, key: idempotencyKey() }
    }
    return operation.current.key
  }

  const finish = () => {
    setAdding(false)
    setEditing(null)
    setDraft('')
    setMutationError(null)
    operation.current = null
    load()
  }

  const save = (note?: InternalNote) => {
    const body = draft.trim()
    if (!body || saving) return
    setSaving(true)
    setMutationError(null)
    const signature = `${note?.noteRef ?? 'create'}:${note?.revision ?? 0}:${body}`
    const pending = note
      ? repository.edit(
          tenantId,
          aggregateId,
          note.noteRef,
          note.revision,
          keyFor(signature),
          body
        )
      : repository.create(tenantId, aggregateId, keyFor(signature), body)
    void pending
      .then(finish)
      .catch((error) => {
        setMutationError(
          error instanceof ApiError && error.status === 409
            ? 'This note changed elsewhere. Refresh and try again.'
            : 'The note could not be saved. Try again.'
        )
      })
      .finally(() => setSaving(false))
  }

  const remove = (note: InternalNote) => {
    if (saving) return
    setSaving(true)
    setMutationError(null)
    const signature = `delete:${note.noteRef}:${note.revision}`
    void repository
      .remove(
        tenantId,
        aggregateId,
        note.noteRef,
        note.revision,
        keyFor(signature)
      )
      .then(finish)
      .catch((error) => {
        setMutationError(
          error instanceof ApiError && error.status === 409
            ? 'This note changed elsewhere. Refresh and try again.'
            : 'The note could not be deleted. Try again.'
        )
      })
      .finally(() => setSaving(false))
  }

  if (state.status === 'loading') {
    return <p className={styles.contextMessage}>Loading internal notes…</p>
  }

  return (
    <div className={styles.internalNotes}>
      {state.status === 'error' ? (
        <div className={styles.noteError} role="alert">
          Internal notes are unavailable.
          <Button type="button" variant="ghost" onClick={() => load()}>
            Retry
          </Button>
        </div>
      ) : null}
      {state.notes.length === 0 && state.status === 'ready' ? (
        <p className={styles.contextMessage}>No internal notes yet.</p>
      ) : null}
      <div className={styles.noteList}>
        {state.notes.map((note) => (
          <article className={styles.noteItem} key={note.noteRef}>
            {editing === note.noteRef ? (
              <div className={styles.noteEditor}>
                <label htmlFor={`edit-${note.noteRef}`}>
                  Edit internal note
                </label>
                <textarea
                  id={`edit-${note.noteRef}`}
                  maxLength={2000}
                  rows={4}
                  value={draft}
                  onChange={(event) => {
                    setDraft(event.target.value)
                    setMutationError(null)
                    operation.current = null
                  }}
                />
                <div className={styles.noteActions}>
                  <Button
                    type="button"
                    onClick={() => save(note)}
                    disabled={!draft.trim() || saving}
                  >
                    <Check size={14} aria-hidden="true" /> Save
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setEditing(null)}
                    disabled={saving}
                  >
                    <X size={14} aria-hidden="true" /> Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p>{note.body}</p>
                <footer>
                  <span>
                    {note.authorLabel} · {formatTimestamp(note.updatedAtMs)}
                  </span>
                  {note.editable ? (
                    <span className={styles.noteButtons}>
                      <button
                        type="button"
                        aria-label="Edit internal note"
                        onClick={() => {
                          setEditing(note.noteRef)
                          setAdding(false)
                          setDraft(note.body)
                          setMutationError(null)
                          operation.current = null
                        }}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        aria-label="Delete internal note"
                        disabled={saving}
                        onClick={() => remove(note)}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </span>
                  ) : null}
                </footer>
              </>
            )}
          </article>
        ))}
      </div>
      {state.cursor && state.status === 'ready' ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => load(state.cursor ?? undefined)}
        >
          Load older notes
        </Button>
      ) : null}
      {adding ? (
        <div className={styles.noteEditor}>
          <label htmlFor="new-internal-note">New internal note</label>
          <textarea
            id="new-internal-note"
            maxLength={2000}
            rows={4}
            autoFocus
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
              setMutationError(null)
              operation.current = null
            }}
            placeholder="Add staff-only operational context"
          />
          <div className={styles.noteActions}>
            <Button
              type="button"
              onClick={() => save()}
              disabled={!draft.trim() || saving}
            >
              <Check size={14} aria-hidden="true" />{' '}
              {saving ? 'Saving…' : 'Save note'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setAdding(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setAdding(true)
            setEditing(null)
            setDraft('')
            setMutationError(null)
            operation.current = null
          }}
        >
          <Plus size={14} aria-hidden="true" /> Add note
        </Button>
      )}
      {mutationError ? (
        <p className={styles.noteMutationError} role="alert">
          {mutationError}
        </p>
      ) : null}
    </div>
  )
}
